import React, { useState } from 'react';
import { Button, Typography, Alert, Space, Card } from 'antd';
import { ReloadOutlined, InfoCircleOutlined, MailOutlined } from '@ant-design/icons';
import { ReactComponent as BusableLogo } from '../assets/images/busable-logo-symbol.svg';
import dayjs from '../dayjs';
import { routesModelExpiryService } from '../services/ModelService';

const { Title, Text, Paragraph } = Typography;

function VersionCheckPage({ 
    version, 
    releaseNotesUrl, 
    onProceed, 
    onClearCache,
    messageApi 
}) {
    const [isClearing, setIsClearing] = useState(false);

    const handleClearCache = async () => {
        setIsClearing(true);
        try {
            await routesModelExpiryService.deleteAllDbs()
            messageApi.success('Cache cleared successfully... Please login again.');
        } catch (error) {
            console.error('Error clearing cache:', error);
            messageApi.error('Failed to clear cache. Please try again or contact support.');
        } finally {
            setIsClearing(false);
            setTimeout(() => {
                onClearCache();
            }, 1000);
        }
    };

    const handleProceed = () => {
        messageApi.info('Proceeding without clearing cache. If you experience any issues, please clear your cache and try again.');
        onProceed();
    };

    const supportEmail = `mailto:support@busable.app?subject=Busable%20version%20issue%20${version}%20${encodeURIComponent(dayjs().format('lll'))}`;

    return (
        <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f8f9fa'
        }}>
            <Card 
                style={{ 
                    maxWidth: '600px', 
                    width: '100%',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px'
                }}
            >
                <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                    <BusableLogo 
                        style={{ 
                            width: '120px', 
                            height: '120px',
                            margin: '0 auto 20px',
                            display: 'block'
                        }} 
                    />
                    <div style={{ 
                        fontSize: '18px', 
                        color: '#6B54F9', 
                        fontWeight: '500',
                        marginBottom: '10px'
                    }}>
                        New Version Available
                    </div>
                </div>

                <Title level={2} style={{ marginBottom: '16px', color: '#6B54F9' }}>
                    Welcome to Busable v{version}!
                </Title>

                <Paragraph style={{ fontSize: '16px', marginBottom: '24px', color: '#666' }}>
                    A new version of Busable has been detected. To ensure the best experience and avoid any compatibility issues, 
                    we recommend clearing your browser cache before proceeding.
                </Paragraph>

                <Alert
                    message="Version Update Notice"
                    description={
                        <div>
                            <Text strong>What's new in v{version}?</Text>
                            <br />
                            <a href={releaseNotesUrl} target="_blank" rel="noreferrer noopener">
                                View release notes and new features
                            </a>
                        </div>
                    }
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginBottom: '24px', textAlign: 'left' }}
                />

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={handleClearCache}
                        loading={isClearing}
                        style={{
                            height: '48px',
                            fontSize: '16px',
                            backgroundColor: '#6B54F9',
                            borderColor: '#6B54F9',
                            borderRadius: '8px',
                            width: '100%'
                        }}
                    >
                        {isClearing ? 'Clearing Cache...' : 'Continue with v'+version}
                    </Button>

                </Space>

                <div style={{ 
                    marginTop: '32px', 
                    padding: '16px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: '8px',
                    border: '1px solid #b3d9ff'
                }}>
                    <Text style={{ fontSize: '14px', color: '#0066cc' }}>
                        <MailOutlined style={{ marginRight: '8px' }} />
                        Having issues? Contact our support team at{' '}
                        <a href={supportEmail} style={{ color: '#0066cc', textDecoration: 'underline' }}>
                            support@busable.app
                        </a>
                    </Text>
                </div>
            </Card>
        </div>
    );
}

export default VersionCheckPage;
